import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { ProjectMember } from "@/lib/api-types";

/**
 * Project members hooks — locked 2026-07-14, migration 0011.
 *
 * All three endpoints are gated ADMIN + FULL_ACCESS server-side
 * (ACTIONS.CREATE_PROJECT). Rendering the invite dialog should also gate on
 * canCreateProject() so we don't show a form that will 403 on submit.
 *
 * Query key convention: ["project-members", projectId]. Invalidated by both
 * invite + remove so the dialog's list stays fresh. Projects list is ALSO
 * invalidated because `isProjectMember` on the caller changes when they add
 * themselves (in theory — today the endpoint targets other users, but keep
 * the invalidation to survive future changes).
 */
const membersKey = (projectId: string) => ["project-members", projectId] as const;

/** GET /api/editor/projects/:id/members */
export function useProjectMembers(projectId: string | undefined, enabled = true) {
  return useQuery<ProjectMember[]>({
    queryKey: membersKey(projectId ?? ""),
    queryFn: () => api.get<ProjectMember[]>(`/editor/projects/${projectId}/members`),
    enabled: !!projectId && enabled,
  });
}

/**
 * POST /api/editor/projects/:id/members — add an existing workspace member.
 * Idempotent server-side. Response includes `alreadyMember: true` on no-op.
 */
export function useInviteProjectMember(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.post<{ ok: true; alreadyMember?: boolean }>(
        `/editor/projects/${projectId}/members`,
        { userId },
      ),
    onSuccess: () => {
      if (!projectId) return;
      qc.invalidateQueries({ queryKey: membersKey(projectId) });
      qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

/**
 * DELETE /api/editor/projects/:id/members/:userId — remove a member.
 * Idempotent. Does not affect the user's workspace role.
 */
export function useRemoveProjectMember(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.delete<{ ok: true }>(`/editor/projects/${projectId}/members/${userId}`),
    onSuccess: () => {
      if (!projectId) return;
      qc.invalidateQueries({ queryKey: membersKey(projectId) });
      qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}
